import type { LayoutFieldsSetupType } from "@whatslly/types/LayoutFieldsSetupType";
import type { LayoutFieldsSettingsType } from "@whatslly/types/User";
import { TFunction } from "i18next";
import upperFirst from "lodash/upperFirst";

export type SelectOptionSingleType = { label: string; value: keyof LayoutFieldsSetupType };

export const OPTION_VALUES = {
  LEAD: "lead",
  CONTACT: "contact",
  ACCOUNT: "account",
  ACTIVITY: "activity",
  TASK: "task",
  CASE: "case",
  OPPORTUNITY: "opportunity",
  EVENT: "event",
  PERSON_ACCOUNT: "personAccount",

  NEW_LEAD: "newLead",
  NEW_CONTACT: "newContact",
  NEW_ACCOUNT: "newAccount",
  NEW_PERSON_ACCOUNT: "newPersonAccount",

  NEW_ACTIVITY: "newActivity",
  NEW_TASK: "newTask",
  NEW_CASE: "newCase",
  NEW_EVENT: "newEvent",
};

export const getDisplayOptions = (t: TFunction, settings?: LayoutFieldsSettingsType) => {
  const options = [];
  if (settings?.showLeads) {
    options.push({ label: t("entity_lead"), value: OPTION_VALUES.LEAD });
  }
  if (settings?.showContacts) {
    options.push({ label: t("entity_contact"), value: OPTION_VALUES.CONTACT });
    options.push({ label: t("entity_account"), value: OPTION_VALUES.ACCOUNT });
  }
  if (settings?.showActivities) {
    options.push({ label: t("entity_activity"), value: OPTION_VALUES.ACTIVITY });
  }
  if (settings?.showTasks) {
    options.push({ label: t("entity_task"), value: OPTION_VALUES.TASK });
  }
  if (settings?.showCases) {
    options.push({ label: t("entity_case"), value: OPTION_VALUES.CASE });
  }
  if (settings?.showOpps) {
    options.push({ label: t("entity_opps"), value: OPTION_VALUES.OPPORTUNITY });
  }
  if (settings?.showCalendar) {
    options.push({ label: t("action_button_event"), value: OPTION_VALUES.EVENT });
  }
  if (settings?.showPersonAccount) {
    options.push({ label: t("entity_person_account"), value: OPTION_VALUES.PERSON_ACCOUNT });
  }
  if (settings?.showCustomObject && Object.keys(settings?.showCustomObject).length > 0) {
    Object.keys(settings.showCustomObject).forEach((key) => {
      options.push({ label: upperFirst(settings.showCustomObject?.[key].text || key), value: key });
    });
  }
  return options as { label: string; value: keyof LayoutFieldsSetupType }[];
};

export const getFormLayoutsOptions = (t: TFunction, settings?: LayoutFieldsSettingsType): { label: string; value: keyof LayoutFieldsSetupType }[] => {
  const options = [];

  if (settings?.showLeads && (settings?.createLeads || settings?.editLead)) {
    options.push({ label: t("entity_new_lead"), value: OPTION_VALUES.NEW_LEAD });
  }
  if (settings?.showContacts && (settings?.createContacts || settings?.editContact)) {
    options.push({ label: t("entity_new_contact"), value: OPTION_VALUES.NEW_CONTACT });
  }
  if ((settings?.showContacts && settings?.createContacts) || settings?.createBusinessAccount || settings?.editBusinessAccount) {
    options.push({ label: t("entity_new_account"), value: OPTION_VALUES.NEW_ACCOUNT });
  }
  if (settings?.createPersonAccount || settings?.editPersonAccount) {
    options.push({ label: t("entity_new_person_account"), value: OPTION_VALUES.NEW_PERSON_ACCOUNT });
  }
  if (settings?.createActivities || settings?.editActivity) {
    options.push({ label: t("entity_new_activity"), value: OPTION_VALUES.NEW_ACTIVITY });
  }
  if (settings?.createTasks || settings?.editTask) {
    options.push({ label: t("entity_new_task"), value: OPTION_VALUES.NEW_TASK });
  }
  if (settings?.createCases || settings?.editCase) {
    options.push({ label: t("entity_new_case"), value: OPTION_VALUES.NEW_CASE });
  }
  if (settings?.showCalendar && settings?.actions.meeting === "show") {
    options.push({ label: t("entity_new_event"), value: OPTION_VALUES.NEW_EVENT });
  }

  if (settings?.createCustomObject && Object.keys(settings?.createCustomObject).length > 0) {
    Object.keys(settings.createCustomObject).forEach((key) => {
      options.push({ label: `${t("new_word")} ${upperFirst(settings.createCustomObject?.[key].text || key)}`, value: `new${upperFirst(key)}` });
    });
  }

  return options as { label: string; value: keyof LayoutFieldsSetupType }[];
};

export const fieldsToJs = (currentFieldValue: any[]) => {
  try {
    return JSON.parse(JSON.stringify(currentFieldValue || []));
  } catch {
    return [];
  }
};

export const addEmptyArrayToNotExistsFieldsType = (layoutFieldsSetupType: LayoutFieldsSetupType) => {
  return Object.keys(layoutFieldsSetupType).reduce((fields, fieldName) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentFieldValue = layoutFieldsSetupType[fieldName as any];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fields[fieldName as any] = Array.isArray(currentFieldValue) ? fieldsToJs(currentFieldValue) : [];
    return fields;
  }, {} as LayoutFieldsSetupType);
};
